import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import type {
  AdminApi,
  NativePilotTrustedEmailSettings,
  NativeTrustedEmail,
  NativeTrustedEmailBulkResult,
  NativeLegacyTrustedEmailImportResult,
} from './admin-api';
import { problemMessage } from './admin-presentation';

const PAGE_SIZES = [25, 50, 100] as const;

function IconEdit(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M11.7 1.3a1 1 0 0 1 1.4 0l1.6 1.6a1 1 0 0 1 0 1.4L6.4 12.6 2 14l1.4-4.4L11.7 1.3zm-8 10.2.6 1.8 1.8-.6 6.7-6.7-1.2-1.2-6.9 6.7z"
      />
    </svg>
  );
}

function IconExclude(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M4 2h8a1 1 0 0 1 1 1v1H3V3a1 1 0 0 1 1-1zm1 4h6v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6zm2 1v5h1V7H7zm2 0v5h1V7H9zM2 4h12v1H2V4z"
      />
    </svg>
  );
}

function IconSave(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M3 1h8l3 3v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zm1 1v4h6V2H4zm7 0v3h2.2L11 2zM4 8v5h8V8H4z"
      />
    </svg>
  );
}

function IconCancel(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M4.3 3.3a1 1 0 0 1 1.4 0L8 5.6l2.3-2.3a1 1 0 1 1 1.4 1.4L9.4 7l2.3 2.3a1 1 0 0 1-1.4 1.4L8 8.4l-2.3 2.3a1 1 0 0 1-1.4-1.4L6.6 7 4.3 4.7a1 1 0 0 1 0-1.4z"
      />
    </svg>
  );
}

function IconActionButton(props: {
  readonly label: string;
  readonly disabled?: boolean;
  readonly tone?: 'default' | 'danger';
  readonly onClick: () => void;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      className={
        props.tone === 'danger' ? 'pkg-a-icon-btn pkg-a-icon-btn-danger' : 'pkg-a-icon-btn'
      }
      title={props.label}
      aria-label={props.label}
      disabled={props.disabled === true}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}

function accessStatusText(source: 'legacy_env' | 'db'): string {
  return source === 'db'
    ? 'Доступ регулируется этим списком'
    : 'Доступ пока регулируется прежним списком';
}

function formatBulkNotice(result: NativeTrustedEmailBulkResult): string {
  return [
    `Добавлено: ${result.added}`,
    `возвращено в список: ${result.restored}`,
    `уже были в списке: ${result.already_active}`,
    `повторов во вставке: ${result.duplicate_in_batch}`,
    `с ошибкой: ${result.invalid.length}`,
  ].join('; ');
}

function formatImportNotice(result: NativeLegacyTrustedEmailImportResult): string {
  if (result.dry_run) {
    return [
      `Проверка переноса: в прежнем списке ${result.env_email_count}`,
      `к добавлению ${result.would_add}`,
      `к восстановлению ${result.would_restore}`,
      `уже в новом списке ${result.would_already_active}`,
    ].join('; ');
  }
  return [
    `Перенос выполнен: добавлено ${result.applied_add}`,
    `восстановлено ${result.applied_restore}`,
    `уже были ${result.applied_already_active}`,
  ].join('; ');
}

export function TrustedContractorEmailsAdmin(props: {
  readonly api: AdminApi;
}): ReactElement {
  const [settings, setSettings] = useState<NativePilotTrustedEmailSettings | null>(null);
  const [items, setItems] = useState<readonly NativeTrustedEmail[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(25);
  const [draftQuery, setDraftQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [singleEmail, setSingleEmail] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<NativeTrustedEmailBulkResult | null>(null);
  const [importResult, setImportResult] = useState<NativeLegacyTrustedEmailImportResult | null>(
    null,
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingEmail, setEditingEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const seq = useRef(0);

  const reload = (nextPage = page, nextPageSize = pageSize, nextQuery = appliedQuery) => {
    const requestSeq = seq.current + 1;
    seq.current = requestSeq;
    setLoading(true);
    setError(null);
    void Promise.all([
      props.api.getNativeTrustedEmailPilotSettings(),
      props.api.listNativeTrustedEmails({
        q: nextQuery,
        page: nextPage,
        pageSize: nextPageSize,
      }),
    ])
      .then(([pilotSettings, list]) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setSettings(pilotSettings);
        setItems(list.items);
        setTotal(list.total);
        setPage(list.page);
        setPageSize(list.page_size as (typeof PAGE_SIZES)[number]);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq.current !== requestSeq) {
          return;
        }
        setError(problemMessage(failure));
        setLoading(false);
      });
  };

  useEffect(() => {
    reload(1, pageSize, appliedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount/query reload
  }, [props.api]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const listIsAuthoritative = settings?.trusted_email_source === 'db';

  const runBusy = async (action: () => Promise<void>): Promise<void> => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (failure: unknown) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const onAddOne = (event: FormEvent) => {
    event.preventDefault();
    void runBusy(async () => {
      await props.api.addNativeTrustedEmail(singleEmail);
      setSingleEmail('');
      setNotice(
        listIsAuthoritative
          ? 'Адрес добавлен. Подрядчик сможет запросить код входа с этого адреса.'
          : 'Адрес сохранён в новом списке. Пока действует прежний список, вход ещё не изменится.',
      );
      reload(1, pageSize, appliedQuery);
    });
  };

  const onBulk = (event: FormEvent) => {
    event.preventDefault();
    void runBusy(async () => {
      const result = await props.api.bulkAddNativeTrustedEmails(bulkText);
      setBulkResult(result);
      setNotice(formatBulkNotice(result));
      if (result.invalid.length === 0) {
        setBulkText('');
      }
      reload(1, pageSize, appliedQuery);
    });
  };

  const onSaveEdit = (row: NativeTrustedEmail) => {
    void runBusy(async () => {
      await props.api.updateNativeTrustedEmail(row.id, editingEmail, row.version);
      setEditingId(null);
      setEditingEmail('');
      setNotice('Адрес обновлён.');
      reload(page, pageSize, appliedQuery);
    });
  };

  const onRevoke = (row: NativeTrustedEmail) => {
    const confirmText = listIsAuthoritative
      ? `Исключить ${row.email}? После этого подрядчик не сможет войти с этого адреса.`
      : `Убрать ${row.email} из нового списка? Пока действует прежний список, текущий вход не изменится.`;
    if (!window.confirm(confirmText)) {
      return;
    }
    void runBusy(async () => {
      await props.api.revokeNativeTrustedEmail(row.id, row.version);
      setNotice(
        listIsAuthoritative
          ? 'Адрес исключён. Вход с него больше не разрешён.'
          : 'Адрес убран из нового списка.',
      );
      reload(page, pageSize, appliedQuery);
    });
  };

  const onImport = (dryRun: boolean) => {
    void runBusy(async () => {
      const result = await props.api.importNativeTrustedEmailsFromLegacyEnv(dryRun);
      setImportResult(result);
      setNotice(formatImportNotice(result));
      if (!dryRun) {
        reload(page, pageSize, appliedQuery);
      }
    });
  };

  const onCutover = (target: 'db' | 'legacy_env') => {
    const confirmText =
      target === 'db'
        ? 'Включить этот список для входа подрядчиков? Перед этим все адреса из прежнего списка должны быть уже перенесены. Продолжить?'
        : 'Вернуться к прежнему списку доступа? Изменения в новом списке перестанут влиять на вход. Продолжить?';
    if (!window.confirm(confirmText)) {
      return;
    }
    void runBusy(async () => {
      const next = await props.api.cutoverNativeTrustedEmailSource(target, true);
      setSettings(next);
      setNotice(accessStatusText(next.trusted_email_source));
    });
  };

  return (
    <div data-testid="trusted-emails-admin">
      <section className="pkg-a-section" aria-labelledby="trusted-emails-heading">
        <h2 id="trusted-emails-heading">Доверенные адреса подрядчиков</h2>
        <p className="page-description">
          Войти в кабинет могут только подрядчики с разрешённым адресом электронной почты. Для входа
          потребуется код из письма.
        </p>
        {settings !== null ? (
          <p className="pkg-a-hint" data-testid="trusted-emails-access-status" role="status">
            {accessStatusText(settings.trusted_email_source)}
            {settings.trusted_email_source === 'legacy_env'
              ? ' Изменения в таблице ниже пока не меняют фактический вход — сначала завершите перенос в дополнительных настройках.'
              : null}
          </p>
        ) : null}
      </section>

      <section className="pkg-a-section" aria-label="Добавление адресов">
        <h3>Добавить адрес</h3>
        <form className="pkg-a-form trusted-email-add-form" onSubmit={onAddOne}>
          <label htmlFor="trusted_email_single">
            Адрес электронной почты
            <input
              id="trusted_email_single"
              type="email"
              autoComplete="off"
              value={singleEmail}
              disabled={busy}
              onChange={(event) => setSingleEmail(event.target.value)}
              required
            />
          </label>
          <button type="submit" disabled={busy || singleEmail.trim() === ''}>
            Добавить
          </button>
        </form>

        <div className="trusted-email-bulk">
          <button
            type="button"
            className="pkg-a-registry-link"
            aria-expanded={bulkOpen}
            onClick={() => setBulkOpen((open) => !open)}
          >
            {bulkOpen ? 'Скрыть добавление нескольких адресов' : 'Добавить несколько адресов'}
          </button>
          {bulkOpen ? (
            <form className="pkg-a-form" onSubmit={onBulk}>
              <label htmlFor="trusted_email_bulk">
                Добавить несколько адресов
                <textarea
                  id="trusted_email_bulk"
                  rows={4}
                  value={bulkText}
                  disabled={busy}
                  onChange={(event) => setBulkText(event.target.value)}
                  required
                />
              </label>
              <p className="pkg-a-hint">
                Вставьте адреса по одному на строку. Можно также разделять их запятыми или точкой с
                запятой.
              </p>
              <button type="submit" disabled={busy || bulkText.trim() === ''}>
                Добавить адреса
              </button>
            </form>
          ) : null}
          {bulkResult !== null && bulkResult.invalid.length > 0 ? (
            <div data-testid="trusted-emails-bulk-invalid">
              <p>Не удалось принять часть адресов ({bulkResult.invalid.length}):</p>
              <ul>
                {bulkResult.invalid.map((row) => (
                  <li key={`${row.raw}:${row.reason}`}>
                    {row.raw} — {row.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      <section className="pkg-a-section" aria-label="Список адресов">
        <h3>{listIsAuthoritative ? 'Адреса в списке доступа' : 'Подготовленный список адресов'}</h3>
        <form
          className="pkg-a-filter-bar"
          onSubmit={(event) => {
            event.preventDefault();
            setAppliedQuery(draftQuery);
            reload(1, pageSize, draftQuery);
          }}
        >
          <label htmlFor="trusted_email_search">
            Поиск
            <input
              id="trusted_email_search"
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
            />
          </label>
          <label htmlFor="trusted_email_page_size">
            На странице
            <select
              id="trusted_email_page_size"
              value={pageSize}
              onChange={(event) => {
                const next = Number(event.target.value) as (typeof PAGE_SIZES)[number];
                setPageSize(next);
                reload(1, next, appliedQuery);
              }}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <div className="pkg-a-filter-actions">
            <button type="submit">Найти</button>
            <button
              type="button"
              onClick={() => {
                setDraftQuery('');
                setAppliedQuery('');
                reload(1, pageSize, '');
              }}
            >
              Сбросить
            </button>
          </div>
        </form>

        {error !== null ? (
          <p role="alert" className="pkg-a-error">
            {error}
          </p>
        ) : null}
        {notice !== null ? (
          <p role="status" className="pkg-a-hint" data-testid="trusted-emails-notice">
            {notice}
          </p>
        ) : null}
        {loading ? <p>Загрузка…</p> : null}
        {!loading && items.length === 0 ? (
          <p data-testid="trusted-emails-empty">В списке пока нет адресов.</p>
        ) : null}

        {!loading && items.length > 0 ? (
          <>
            <div className="table-wrap">
              <table className="data-table pkg-a-table">
                <thead>
                  <tr>
                    <th scope="col">Адрес</th>
                    <th scope="col">Обновлён</th>
                    <th scope="col">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} data-testid={`trusted-email-row-${row.id}`}>
                      <td>
                        {editingId === row.id ? (
                          <input
                            aria-label="Новый адрес электронной почты"
                            value={editingEmail}
                            disabled={busy}
                            onChange={(event) => setEditingEmail(event.target.value)}
                          />
                        ) : (
                          row.email
                        )}
                      </td>
                      <td>{new Date(row.updated_at).toLocaleString('ru-RU')}</td>
                      <td>
                        <div className="pkg-a-row-actions">
                          {editingId === row.id ? (
                            <>
                              <IconActionButton
                                label="Сохранить"
                                disabled={busy}
                                onClick={() => onSaveEdit(row)}
                              >
                                <IconSave />
                              </IconActionButton>
                              <IconActionButton
                                label="Отменить"
                                disabled={busy}
                                onClick={() => {
                                  setEditingId(null);
                                  setEditingEmail('');
                                }}
                              >
                                <IconCancel />
                              </IconActionButton>
                            </>
                          ) : (
                            <>
                              <IconActionButton
                                label="Изменить"
                                disabled={busy}
                                onClick={() => {
                                  setEditingId(row.id);
                                  setEditingEmail(row.email);
                                }}
                              >
                                <IconEdit />
                              </IconActionButton>
                              <IconActionButton
                                label="Исключить"
                                tone="danger"
                                disabled={busy}
                                onClick={() => onRevoke(row)}
                              >
                                <IconExclude />
                              </IconActionButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pkg-a-filter-actions" data-testid="trusted-emails-pager">
              <button
                type="button"
                disabled={busy || page <= 1}
                onClick={() => reload(page - 1, pageSize, appliedQuery)}
              >
                Назад
              </button>
              <span>
                Страница {page} из {pageCount} (всего {total})
              </span>
              <button
                type="button"
                disabled={busy || page >= pageCount}
                onClick={() => reload(page + 1, pageSize, appliedQuery)}
              >
                Вперёд
              </button>
            </div>
          </>
        ) : null}
      </section>

      {settings !== null ? (
        <details className="pkg-a-section trusted-email-advanced" data-testid="trusted-emails-advanced">
          <summary>Настройка перехода на новый список</summary>
          <p className="pkg-a-hint" data-testid="trusted-emails-cutover-hint">
            Сначала проверьте и перенесите адреса из прежнего списка. Включать новый список можно
            только когда все нужные адреса уже перенесены. Вернуться к прежнему списку нельзя, если
            он пуст — иначе вход будет закрыт для всех.
          </p>
          <p>
            В новом списке сейчас адресов: <strong>{settings.active_trusted_email_count}</strong>.
          </p>
          <ol className="trusted-email-advanced-steps">
            <li>
              <button type="button" disabled={busy} onClick={() => onImport(true)}>
                Проверить перенос
              </button>
            </li>
            <li>
              <button type="button" disabled={busy} onClick={() => onImport(false)}>
                Перенести адреса
              </button>
            </li>
            <li>
              <button
                type="button"
                disabled={busy || settings.trusted_email_source === 'db'}
                onClick={() => onCutover('db')}
              >
                Использовать список системы
              </button>
            </li>
            <li>
              <button
                type="button"
                disabled={busy || settings.trusted_email_source === 'legacy_env'}
                onClick={() => onCutover('legacy_env')}
              >
                Вернуться к прежнему списку
              </button>
            </li>
          </ol>
          {importResult !== null ? (
            <p className="pkg-a-hint" data-testid="trusted-emails-import-result">
              {formatImportNotice(importResult)}
            </p>
          ) : null}
        </details>
      ) : null}
    </div>
  );
}
